<template>
  <div>
    <h1 class="mb-8 font-bold text-3xl">
      <inertia-link class="text-shtax-400 hover:text-shtax-600" :href="route('clients')">Klienci</inertia-link>
      <span class="text-shtax-400 font-medium">/</span>
      <inertia-link class="text-shtax-400 hover:text-shtax-600" :href="route('clients.show', client.id)">{{ firma }}</inertia-link>
      <span class="text-shtax-400 font-medium">/</span>
      <inertia-link class="text-shtax-400 hover:text-shtax-600" :href="route('clients.show', client.id)">Definicje faktur</inertia-link>
      <span class="text-shtax-400 font-medium">/</span>
      {{ facture.id }}
    </h1>

    <blocked-message v-if="facture.deleted_at" class="mb-6" @restore="restore">
      Definicja faktury ma włączoną blokadę wystawiania.
    </blocked-message>

    <div class="flex flex-wrap">
      <div class="bg-white rounded-md shadow overflow-hidden w-full lg:w-1/2">
        <form @submit.prevent="update">
          <div class="p-8 -mr-6 -mb-8 flex flex-wrap">
            
            <text-input v-model="firma" label="Klient" class="pr-6 pb-8 w-full" disabled />
            
            <multiselect-input v-model="form.contract" :error="form.errors.contract" label="Umowa" :options="contracts" class="pr-6 pb-8 w-full" track_by="id" opt_label="number" :multiple="false" :close_on_select="true" />

            <multiselect-input v-model="form.storeroom" :error="form.errors.storeroom" label="Magazyn" :options="storerooms" class="pr-6 pb-8 w-full lg:w-1/2" track_by="id" opt_label="name" :multiple="false" :close_on_select="true" />

            <multiselect-input v-model="form.payment_method" :error="form.errors.payment_method" label="Forma płatności" :options="payment_methods" class="pr-6 pb-8 w-full lg:w-1/2" track_by="id" opt_label="name" :multiple="false" :close_on_select="true" :searchable="true" />

            <shtax-text-area-input class="pr-6 pb-8 w-full" v-model:checked="form.notice" :error="form.errors.notice" label="Uwagi"  />

            <div class="w-full lg:w-1/2">
              Liczba dokumentów zgromadzonych przez klienta w miesiącu <b>{{ last_month }}</b> wynosi: <b>{{ liczba_dok }}</b>
            </div>
          </div>
          <div class="px-8 py-4 bg-gray-50 border-t border-gray-100 flex items-center">
            <button v-if="!facture.deleted_at && $page.props.can['delete factures']" class="text-red-600 hover:underline mr-5" tabindex="-1" type="button" @click="destroy">Zablokuj fakturę</button>
            <button v-if="facture.deleted_at && $page.props.can['hard delete factures']" class="text-red-600 hover:underline mr-5" tabindex="-1" type="button" @click="hardDestroy">Usuń całkowicie</button>
            <loading-button :loading="form.processing" class="btn-shtax ml-auto" type="submit">Zaktualizuj dane</loading-button>
          </div>
        </form>
      </div>

      <div class="mt-12 lg:mt-0 lg:pl-8 w-full lg:w-1/2">
        <h3 class="mb-2 font-bold text-lg md:text-md ">
          Historia zgromadzonych przez klienta dokumentów <small class="text-gray-400">(z ostatnich 12 miesięcy)</small>
        </h3>
        <div class="mt-2 bg-white rounded shadow overflow-x-auto text-sm">
          <table class="w-full whitespace-nowrap">
            <tr class="text-left font-bold">
              <th class="px-3 pt-3 pb-2">Rok</th>
              <th class="px-3 pt-3 pb-2">Miesiąc</th>
              <th class="px-3 pt-3 pb-2">Liczba dokumentów</th>
            </tr>
            <tr v-for="doc in docs" class="hover:bg-gray-100 focus-within:bg-gray-100">
              <td class="border-t">
                <span class="px-3 py-2 flex items-center focus:text-shtax-500">
                  {{ doc.ROK }}
                </span>
              </td>
              <td class="border-t">
                <span class="px-3 py-2 flex items-center" tabindex="-1">
                  {{ doc.MIESIAC }}
                </span>
              </td>
              <td class="border-t">
                <span class="px-3 py-2 flex items-center" tabindex="-1">
                  {{ doc.number_of_docs }}
                </span>
              </td>
            </tr>
            <tr v-if="docs.length === 0">
              <td class="border-t px-3 py-2" colspan="3">Klient nie zgromadził jeszcze żadnych dokumentów.</td>
            </tr>
          </table>
        </div>
      </div>
    </div>

    <h2 class="mt-12 font-bold text-xl md:text-2xl relative">
        Pozycje zdefiniowanej faktury
        <div class="inline">
            <inertia-link class="btn-shtax ml-auto absolute right-0 top-0 disabled:opacity-50" :href="route('factures.positions.create', {'facture': facture.id})" v-if="!facture.deleted_at">
              <span>Nowa</span>
              <span class="hidden md:inline">pozycja</span>
            </inertia-link>
        </div>
    </h2>

    <div class="mt-6 bg-white rounded shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="px-6 pt-6 pb-4">Cykl</th>
          <th class="px-6 pt-6 pb-4">Data wystawienia</th>
          <th class="px-6 pt-6 pb-4">Artykuł</th>
          <th class="px-6 pt-6 pb-4">Ilość</th>
          <th class="px-6 pt-6 pb-4">Cena netto</th>
          <th class="px-6 pt-6 pb-4">VAT</th>
          <th class="px-6 pt-6 pb-4">Cena brutto</th>
          <th class="px-6 pt-6 pb-4" colspan="2">Opis</th>
        </tr>
        <tr v-for="position in facture.positions" :key="position.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('factures.positions.edit', {'facture':facture.id, 'position':position.id})">
              <span>
                {{ position.cycle.name }}
              </span>
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('factures.positions.edit', {'facture':facture.id, 'position':position.id})">
              <span>
                {{ position.cycle_from }}
              </span>
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('factures.positions.edit', {'facture':facture.id, 'position':position.id})">
              <span>
                {{ position.artykul.NAZWA }}
              </span>
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('factures.positions.edit', {'facture':facture.id, 'position':position.id})">
              <span>
                {{ position.ilosc.replace('.', ',') }}
              </span>
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('factures.positions.edit', {'facture':facture.id, 'position':position.id})">
              <span>
                {{ position.cena_netto.replace('.', ',') }}
              </span>
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('factures.positions.edit', {'facture':facture.id, 'position':position.id})">
              <span>
                {{ position.artykul.VAT_SPRZEDAZY }} %
              </span>
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('factures.positions.edit', {'facture':facture.id, 'position':position.id})">
              <span>
                {{ position.cena_brutto.replace('.', ',') }}
              </span>
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-shtax-500" :href="route('factures.positions.edit', {'facture':facture.id, 'position':position.id})">
              <span>
                {{ position.opis }}
              </span>
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-4 flex items-center" :href="route('factures.positions.edit', {'facture':facture.id, 'position':position.id})" tabindex="-1">
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
            </inertia-link>
          </td>
        </tr>
        <tr v-if="facture.positions.length === 0">
          <td class="border-t px-6 py-4" colspan="9">Brak pozycji na fakturze.</td>
        </tr>
      </table>
    </div>

  </div>
</template>

<script>
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import LoadingButton from '@/Shared/LoadingButton'
import ShtaxCheckbox from '@/ShtaxComponents/Checkbox'
import ShtaxTextAreaInput from '@/ShtaxComponents/TextAreaInput'
import MultiselectInput from '@/ShtaxComponents/MultiselectInput'
import BlockedMessage from '@/ShtaxComponents/BlockedMessage'
import Icon from '@/Shared/Icon'
import Swal from 'sweetalert2'

export default {
  metaInfo() {
    return {
      title: `${this.facture.id}`,
    }
  },
  components: {
    LoadingButton,
    TextInput,
    ShtaxCheckbox,
    ShtaxTextAreaInput,
    MultiselectInput,
    BlockedMessage,
    Icon
  },
  layout: Layout,
  props: {
    client: Object,
    facture: Object,
    contracts: Array,
    storerooms: Array,
    payment_methods: Array,
    last_month: String,
    liczba_dok: Number,
    docs: Array
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        contract: this.facture.contract,
        storeroom: this.facture.magazyn,
        payment_method: this.facture.forma_platnosci,
        notice: this.facture.uwagi
      })
    }
  },
  methods: {
    update() {
      this.form.put(this.route('factures.update', {'client':this.client.id, 'facture':this.facture.id}))
    },
    destroy() {
      Swal.fire({
        title: 'Jesteś pewny że chcesz zablokować wybraną fakturę?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Tak, zablokuj!',
        cancelButtonText: 'Nie'
      }).then((result) => {
        if (result.isConfirmed) {
          this.$inertia.delete(this.route('factures.destroy', {'client':this.client.id, 'facture':this.facture.id}))
        }
      })
    },
    hardDestroy() {
      Swal.fire({
        title: 'Jesteś pewny że chcesz całkowicie usunąć wybraną fakturę klienta?',
        text: 'Przywrócenie usuniętej faktury nie będzie już możliwe',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Tak, usuń całkowicie!',
        cancelButtonText: 'Nie'
      }).then((result) => {
        if (result.isConfirmed) {
          this.$inertia.delete(this.route('factures.harddestroy', {'client':this.client.id, 'facture':this.facture.id, 'force':1}))
        }
      })
    },
    restore() {
      Swal.fire({
        title: 'Jesteś pewny że chcesz odblokować wybraną fakturę?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Tak, odblokuj!',
        cancelButtonText: 'Nie'
      }).then((result) => {
        if (result.isConfirmed) {
          this.$inertia.put(this.route('factures.restore', {'client':this.client.id, 'facture':this.facture.id}))
        }
      })
    },
  },

  computed: {
    firma() {
      return this.client.firma ? this.client.firma.NAZWA_PELNA : this.client.nazwa_pelna
    }
  }
}
</script>
